import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { confirmNewsletterSubscription } from 'wasp/client/operations';
import { routes } from 'wasp/client/router';
import { LinkButton } from '../components/ui';
import { type NewsletterConfirmResult } from './confirmation';

type PageState =
  | { status: 'loading' }
  | { status: 'confirmed' | 'already-confirmed' | 'invalid-token' | 'expired' };

function getStatusContent(pageState: PageState) {
  switch (pageState.status) {
    case 'confirmed':
      return {
        title: 'You are subscribed',
        description:
          'Weekly delivery is now active. Your first newsletter will arrive next Monday morning.',
      };
    case 'already-confirmed':
      return {
        title: 'Already subscribed',
        description:
          'Your newsletter subscription is already active. No further action is needed.',
      };
    case 'expired':
      return {
        title: 'This confirmation link has expired',
        description:
          'Confirmation links are valid for 24 hours. Return to your newsletter settings and click "Start Weekly Delivery" to receive a new link.',
      };
    case 'invalid-token':
      return {
        title: 'This confirmation link is invalid',
        description:
          'The link may be incomplete or has already been used. Return to your newsletter settings to request a new confirmation email.',
      };
    default:
      return {
        title: 'Confirming your newsletter…',
        description: 'Please wait while we confirm your subscription.',
      };
  }
}

export default function NewsletterConfirmPage() {
  const [searchParams] = useSearchParams();
  const [pageState, setPageState] = useState<PageState>({ status: 'loading' });

  useEffect(() => {
    let isActive = true;
    const token = searchParams.get('token');

    if (!token) {
      setPageState({ status: 'invalid-token' });
      return () => {
        isActive = false;
      };
    }

    confirmNewsletterSubscription({ token })
      .then((result: NewsletterConfirmResult) => {
        if (!isActive) {
          return;
        }

        setPageState(result as PageState);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }

        setPageState({ status: 'invalid-token' });
      });

    return () => {
      isActive = false;
    };
  }, [searchParams]);

  const content = getStatusContent(pageState);

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-2xl items-center px-4 py-16">
      <div className="w-full rounded-2xl border border-neutral-200 bg-white p-8 text-center shadow-xs dark:border-neutral-800 dark:bg-boxdark">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
          Optimistic News
        </p>
        <h1 className="mt-4 text-3xl font-bold text-neutral-900 dark:text-white">
          {content.title}
        </h1>
        <p className="mt-4 text-base text-neutral-600 dark:text-neutral-300">
          {content.description}
        </p>

        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <LinkButton to={routes.LandingPageRoute.to} fullWidth>
            Back to home
          </LinkButton>
          <LinkButton
            to={routes.NewsletterRoute.to}
            variant="secondary"
            fullWidth
          >
            Manage newsletter
          </LinkButton>
        </div>
      </div>
    </div>
  );
}
